import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BarChart3, ExternalLink, RefreshCw } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { BACKEND_URL } from "../lib/backend";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function AiStatsPage() {
  const [dm] = useDarkMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const y = new Date().getFullYear();
    document.title = `AI in Travel — Key Statistics ${y} (Updated Monthly) | TourismIntel`;
    const desc = document.querySelector('meta[name="description"]');
    const original = desc?.getAttribute("content");
    desc?.setAttribute("content",
      "Key statistics on AI in travel, tourism and hospitality — adoption, market data and trends, extracted monthly from industry news with source links.");
    axios.get(`${BACKEND_URL}/api/stats/ai-travel`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true));
    return () => { if (original) desc?.setAttribute("content", original); };
  }, []);

  useEffect(() => {
    if (!data?.stats?.length) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      "@id": `${window.location.origin}/ai-travel-statistics`,
      name: "AI in Travel — Key Statistics",
      description: "Statistics on AI adoption and impact in travel, tourism and hospitality, extracted from industry news with source attribution. Updated monthly.",
      url: `${window.location.origin}/ai-travel-statistics`,
      ...(data.updated_at ? { dateModified: data.updated_at } : {}),
      license: `${window.location.origin}/data-license.html`,
      creator: { "@type": "Organization", name: "TourismIntel", url: "https://tourismintel.ai" },
    };
    let el = document.getElementById("aistats-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "aistats-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return () => { document.getElementById("aistats-jsonld")?.remove(); };
  }, [data]);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", sub: "text-slate-300", accent: "text-cyan-400" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", sub: "text-[#3b494c]", accent: "text-[#006874]" };

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <PublicPageNav />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10" data-testid="ai-stats-page">
        <header className="mb-10">
          <p className={`category-tag text-xs mb-3 flex items-center gap-2 ${c.accent}`}>
            <BarChart3 className="w-3.5 h-3.5" /> LIVING DATA PAGE
          </p>
          <h1 className={`editorial-heading text-3xl sm:text-4xl font-semibold ${c.text}`} data-testid="ai-stats-page-title">
            AI in travel — key statistics
          </h1>
          <p className={`mt-3 text-sm leading-relaxed ${c.muted}`}>
            Numbers on AI adoption and impact across travel, tourism and hospitality —
            extracted from recent industry reporting, each with its source.
            {data?.updated_at && <> Last updated {fmtDate(data.updated_at)}.</>}
          </p>
        </header>

        {error && <p className={`text-sm ${c.muted}`} data-testid="ai-stats-error">Could not load statistics — please try again later.</p>}
        {!data && !error && (
          <div className="flex justify-center py-16"><RefreshCw className={`w-5 h-5 animate-spin ${c.accent}`} /></div>
        )}
        {data && data.stats.length === 0 && (
          <p className={`text-sm ${c.muted}`} data-testid="ai-stats-empty">
            The first statistics digest is being prepared — check back soon.
          </p>
        )}

        <div className="space-y-4" data-testid="ai-stats-list">
          {(data?.stats || []).map((s, i) => (
            <article key={i} data-testid={`ai-stat-card-${i}`}
              className={`rounded-2xl border p-5 ${c.card} ${c.border}`}>
              <p className={`text-base md:text-lg font-semibold leading-snug ${c.text}`}>{s.stat}</p>
              {s.context && <p className={`mt-2 text-sm leading-relaxed ${c.sub}`}>{s.context}</p>}
              <p className={`mt-3 text-xs flex flex-wrap items-center gap-x-2 gap-y-1 ${c.muted}`}>
                {s.source_name && <span className={`font-semibold ${c.accent}`}>{s.source_name}</span>}
                {s.published_at && <span>{fmtDate(s.published_at)}</span>}
                {s.source_url && (
                  <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:underline underline-offset-2"
                    data-testid={`ai-stat-source-${i}`}>
                    Source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </p>
            </article>
          ))}
        </div>

        <div className={`mt-12 rounded-2xl border p-6 ${c.card} ${c.border}`} data-testid="ai-stats-cta">
          <h2 className={`text-base font-semibold ${c.text}`}>Get the numbers before everyone else</h2>
          <p className={`mt-1 text-sm ${c.muted}`}>
            These statistics come from the intelligence we curate daily.{" "}
            <Link to="/subscribe" className={`font-semibold hover:underline underline-offset-2 ${c.accent}`}>
              Subscribe to the free weekly travel industry newsletter →
            </Link>
          </p>
        </div>
      </main>
      <PublicPageFooter />
    </div>
  );
}
